import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { TabViewModel } from '@core/models/tab-view.model';

@Component({
  selector: 'xemex-tab-bar',
  imports: [MatIconModule, MatRippleModule, TranslateModule],
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabBarComponent implements AfterViewInit {
  tabs = input<Array<TabViewModel>>([]);
  defaultTab = input(0);
  mode = input<'route' | 'index'>('route');
  tabChange = output<string>();

  @ViewChild('tabNav') tabNavRef!: ElementRef<HTMLDivElement>;

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  selectedTabIndex = signal(0);
  isShowNav = signal(false);
  isDisableNavLeft = signal(true);
  isDisableNavRight = signal(false);
  scrollWidth = 200;

  constructor() {
    this.activatedRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (this.mode() !== 'route') return;
      const tabParam = params.get('tab');
      if (tabParam) {
        const tabIndex = this.tabs().findIndex(tab => tab.route === tabParam);
        if (tabIndex !== -1) {
          this.selectedTabIndex.set(tabIndex);
        }
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.setShowNav();
  }

  ngAfterViewInit() {
    this.setShowNav();
    this.initializeTabFromRoute();
  }

  private initializeTabFromRoute() {
    if (this.mode() === 'route') {
      const tabParam = this.activatedRoute.snapshot.paramMap.get('tab');
      if (tabParam) {
        const tabIndex = this.tabs().findIndex(tab => tab.route === tabParam);
        if (tabIndex !== -1) {
          this.selectedTabIndex.set(tabIndex);
          return;
        }
      }
    }
    this.selectedTabIndex.set(this.defaultTab());
  }

  setShowNav() {
    const el = this.tabNavRef?.nativeElement;
    if (!el) return;
    this.isShowNav.set(el.scrollWidth > el.clientWidth);
  }

  onSelectTab(item: TabViewModel) {
    this.selectedTabIndex.set(item.index);
    if (this.mode() === 'route') {
      this.router.navigate(['../', item.route], {
        relativeTo: this.activatedRoute,
        replaceUrl: true,
      });
    }
    this.tabChange.emit(item.route);
  }

  onNavLeft(event: Event) {
    event.stopPropagation();
    const el = this.tabNavRef?.nativeElement;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft - this.scrollWidth, behavior: 'smooth' });
    this.isDisableNavRight.set(false);
    this.isDisableNavLeft.set(el.scrollLeft - this.scrollWidth <= 0);
  }

  onNavRight(event: Event) {
    event.stopPropagation();
    const el = this.tabNavRef?.nativeElement;
    if (!el) return;
    const remainingWidth = el.clientWidth - el.scrollLeft;
    const value = Math.min(remainingWidth, this.scrollWidth);
    el.scrollTo({ left: el.scrollLeft + value, behavior: 'smooth' });
    this.isDisableNavLeft.set(false);
    const widthValid = el.scrollWidth / (el.clientWidth + el.scrollLeft + value);
    this.isDisableNavRight.set(widthValid <= 1);
  }

  isTabSelected(index: number): boolean {
    return this.selectedTabIndex() === index;
  }
}
